import '@Get-Demo/browser-client';
import GuideManager from '../user-guide/script/GuideManager';
import BotWidgetManager from '../bot/script/BotWidgetManager';
import { detectEmbeddedDuplication } from '../user-guide/helpers/embeddedDuplicationDetector';
import { parseCustomParamsFromUrl } from '../bot/helpers/customParams';
import { resolveBot, CandidateBot, VisitorContext } from './resolveBot';
import { answerOwnershipProbes, ownsFloatingWidget } from './frameOwnership';
import {
  getActiveHeadlessController,
  resolveHeadlessAllowedDomains,
  shouldStartHeadless,
  startHeadless
} from './headless';
import { ensureOnSurface, getdemoEmitter } from './getdemoEvents';
import { ChatTrigger, createChatTrigger, ensureChatSurface } from './chatTrigger';
import { createAvailabilityPublisher } from './availability';
import { getVisitorId } from './visitorId';
import { readVisitorEnvironment } from '../segmentation/helpers/visitorEnvironment';
import {
  installIdentifyInterceptor,
  resolveExternalUserId,
  resolveIdentifyTraits
} from './identify';
import {
  ensureInstructionSurface,
  getInstructionSnapshot,
  onInstructionChange
} from './instruction';
import {
  chatModeOf,
  hideChat,
  identifyChat,
  isChatMounted,
  isNewVersion,
  mountChat,
  swapChat
} from './chatV3';

declare global {
  interface Window {
    __getdemoEmbeddedLoaded?: boolean;
  }
}

export {};

const BACKEND_ENDPOINT = import.meta.env.VITE_BACKEND_ENDPOINT;
const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT;
// Where the chat of a version-3 agent lives (get-demo-agent, spec-007, F5): the chat's own
// deploy, not the app. Built in like the endpoints above; a bot of that version is skipped when
// the bundle was built without it.
const CHAT_URL = import.meta.env.VITE_CHAT_URL;
const CONFIG_TTL_MS = 5 * 60 * 1000;
const SWAP_DEBOUNCE_MS = 300;
const URL_POLL_MS = 500;

interface EmbedConfig {
  tourGuide?: { enabled?: boolean };
  copilot?: {
    enabled?: boolean;
    botId?: string | null;
    defaultBotId?: string | null;
    bots?: CandidateBot[];
    disabledBotIds?: string[];
  };
}

const isDebug = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).has('gdDebug');
  } catch {
    return false;
  }
};

// Visitor "who" comes from the identify queue (window.Getdemo.q) plus URL params,
// mirroring BotWidgetManager.readHostCustomParams + the player's parseCustomParamsFromUrl.
const readIdentifyParams = (): Record<string, any> => {
  try {
    const g = (window as any).Getdemo;
    const queue = g && Array.isArray(g.q) ? g.q : [];
    let params: Record<string, any> = {};
    for (const entry of queue) {
      if (Array.isArray(entry) && entry[0] === 'identify') {
        // entry[1] is the stub's `arguments` object (the official snippet does
        // `g.q.push([n, arguments])`), which is array-LIKE but not an Array — Array.isArray is
        // always false for it, so the strict check silently dropped every queued trait. The
        // userId path next door reads entry[1][0] by index, which is why only traits went missing.
        const traits = resolveIdentifyTraits(entry[1] ? Array.prototype.slice.call(entry[1]) : []);
        if (Object.keys(traits).length) {
          params = traits;
        }
      }
    }
    return params;
  } catch {
    return {};
  }
};

// The identify queue (window.Getdemo.q) is transient — the SDK drains it shortly after
// load. So we snapshot "who" into a persistent object: captured from the queue while it
// still has data, and updated directly on any late identify() call. Re-resolutions after
// the queue is emptied (SPA navigation, late login) then still see the visitor's traits.
let identifySnapshot: Record<string, any> = {};
let identifyUserId: string | null = null;

const mergeIdentify = (traits: any): void => {
  if (traits && typeof traits === 'object' && !Array.isArray(traits)) {
    identifySnapshot = { ...identifySnapshot, ...traits };
  }
};

const captureIdentifyUserId = (candidate: unknown): void => {
  const resolved = resolveExternalUserId(candidate);
  if (resolved) {
    identifyUserId = resolved;
  }
};

const readIdentifyUserIdFromQueue = (): void => {
  try {
    const g = (window as any).Getdemo;
    const queue = g && Array.isArray(g.q) ? g.q : [];
    for (const entry of queue) {
      if (Array.isArray(entry) && entry[0] === 'identify') {
        captureIdentifyUserId(entry[1] && entry[1][0]);
      }
    }
  } catch {
    /* queue unavailable */
  }
};

const captureIdentifyFromQueue = (): void => {
  readIdentifyUserIdFromQueue();
  const fromQueue = readIdentifyParams();
  if (Object.keys(fromQueue).length) mergeIdentify(fromQueue);
};

const buildContext = (debug: boolean): VisitorContext => {
  captureIdentifyFromQueue();
  return {
    url: window.location.href,
    customParams: {
      gdVisitorId: getVisitorId(),
      ...(parseCustomParamsFromUrl() || {}),
      ...identifySnapshot
    },
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // O `userId` já estava capturado do identify; só nunca chegava ao avaliador, e por isso toda
    // condição de `USER_ID` reprovava.
    userId: identifyUserId,
    ...readVisitorEnvironment(),
    debug
  };
};

const getScriptTag = (): HTMLScriptElement | null =>
  document.querySelector('script[gd-company-id], script[gd-bot-id]');

const extractBotIdFromPlayerUrl = (src: string): string | null => {
  try {
    const url = new URL(src, window.location.origin);
    const match =
      url.pathname.match(/\/b\/([0-9a-f-]{36})/i) ||
      url.pathname.match(/\/novo-bot\/([0-9a-f-]{36})/i);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
};

const hideEmbeddedIframe = (iframe: HTMLIFrameElement): void => {
  iframe.removeAttribute('src');
  iframe.style.display = 'none';
};

// Formato `iframe` (`<iframe data-gd-src>`): `startBot()` NÃO roda nesse caminho, então a
// semeadura da instrução tem de acontecer aqui também — o player recebe o valor pelo flush do
// `gd-ready` que `setupLateIdentify` já arma na hidratação.
const seedInstructionIntoManager = (): void => {
  ensureInstructionSurface();
  BotWidgetManager.getInstance().setHostInstruction(getInstructionSnapshot());
};

const hydrateEmbeddedIframeUnlessDisabled = (
  disabledBotIds: string[] | undefined,
  debug: boolean
): void => {
  const iframe = document.getElementById('getdemo-bot-player') as HTMLIFrameElement | null;
  if (!iframe) return;
  const baseSrc = iframe.getAttribute('data-gd-src') || iframe.getAttribute('src') || '';
  const embeddedBotId = extractBotIdFromPlayerUrl(baseSrc);
  if (embeddedBotId && Array.isArray(disabledBotIds) && disabledBotIds.includes(embeddedBotId)) {
    hideEmbeddedIframe(iframe);
    if (debug) {
      console.log('[Getdemo] embedded iframe disabled (siteEnabled=false):', embeddedBotId);
    }
    return;
  }
  seedInstructionIntoManager();
  BotWidgetManager.getInstance().hydrateEmbeddedIframeIfPresent();
};

const startGuide = (): void => {
  if (window.__getdemoEmbeddedLoaded) {
    const info = detectEmbeddedDuplication();
    console.error(
      '[Getdemo] O script unificado foi incluído mais de uma vez nesta página ' +
        `(${info.scriptCount} ocorrências detectadas no DOM). ` +
        'Apenas a primeira carga do Tour Guide será inicializada.'
    );
    return;
  }
  window.__getdemoEmbeddedLoaded = true;
  GuideManager.getInstance().start();
};

/**
 * `Getdemo.open()` / `Getdemo.close()` — o acionamento do chat pelo botão do cliente.
 *
 * Singleton de módulo como `identifySnapshot` ao lado: `startBot` e `reResolve` são funções de
 * módulo e precisam do mesmo trigger que a superfície pública pendurou em `window.Getdemo`.
 */
let chatTrigger: ChatTrigger | null = null;

const getChatTrigger = (): ChatTrigger => {
  if (!chatTrigger) {
    const manager = BotWidgetManager.getInstance();
    chatTrigger = createChatTrigger({
      isMounted: () => manager.isMounted(),
      reveal: intent => manager.reveal(intent),
      close: () => manager.closeChat(),
      emitter: getdemoEmitter,
      debug: isDebug
    });
  }
  return chatTrigger;
};

/**
 * Anuncia se HÁ agente para abrir aqui, para o cliente decidir se mostra o botão dele.
 * A regra e a deduplicação vivem em `availability.ts`; aqui ficam só os pontos de chamada:
 * a resolução do load, a re-resolução (identify tardio e navegação SPA) e o caso sem copilot.
 */
const publishAvailability = createAvailabilityPublisher(getdemoEmitter);

const startBot = (
  botId?: string,
  appUrl?: string,
  embedFormat?: string,
  bot?: CandidateBot | null,
  companyId?: string
): void => {
  // Bridge the identify we captured early (snapshot + URL params) into the bot manager BEFORE it
  // builds the iframe URL — its own queue read would come too late (post-fetchConfig) and find the
  // drained queue, so the condomínio code (and any identify trait) would never reach the agent.
  captureIdentifyFromQueue();
  const who = { ...(parseCustomParamsFromUrl() || {}), ...identifySnapshot };
  // A version-3 agent gets the chat, not the player (get-demo-agent, spec-007, F5): its own
  // application at CHAT_URL, next to the page script that runs the agent's calls on this page.
  // Only what the chat has a message for crosses (identify); the instruction stays with the
  // player. Without the URL or the company there is nothing to mount.
  if (bot && isNewVersion(bot)) {
    if (!CHAT_URL || !companyId) {
      if (isDebug()) {
        console.log('[Getdemo] version-3 agent without VITE_CHAT_URL or company — skipped.');
      }
      return;
    }
    identifyChat(who);
    mountChat({ chatUrl: CHAT_URL, botId: bot.id, companyId, mode: chatModeOf(embedFormat) });
    armIdentifyBridge();
    return;
  }
  // A instrução da aplicação segue o MESMO caminho, e pelo mesmo motivo: uma chamada feita antes
  // do loader carregar está na fila, e a fila já foi drenada quando o bot finalmente inicia.
  ensureInstructionSurface();
  const manager = BotWidgetManager.getInstance();
  manager.setHostCustomParams(who);
  manager.setHostInstruction(getInstructionSnapshot());
  // O visitante fechando o chat no modo manual tem de virar evento para o cliente: é com ele
  // que a plataforma dele sabe que pode voltar a oferecer o botão.
  manager.onManualClose = () => getChatTrigger().notifyClosed();
  manager.configure(botId, appUrl, embedFormat, bot?.triggerMode ?? undefined);
  manager.start();
  // Every company that runs the widget gets the late-identify bridge, regardless of how many
  // agents it has or whether it uses targeting rules.
  armIdentifyBridge();
};

const findCandidateBot = (
  config: EmbedConfig | null,
  botId?: string | null
): CandidateBot | null => {
  const bots = config?.copilot?.bots;
  if (!botId || !Array.isArray(bots)) return null;
  return bots.find(b => b.id === botId) || null;
};

// Late identify hook for headless mode: wraps Getdemo.identify so a post-load call (late
// login) updates the snapshot AND re-sends the executor registration with the fresh
// externalUserId. Re-applied on an interval because the SDK may replace window.Getdemo
// during its own init, discarding a previous wrap.
const armHeadlessIdentifyRebroadcast = (): void => {
  const ensureWrapped = () => {
    try {
      const g = (window as any).Getdemo;
      const fn = g && g.identify;
      if (typeof fn !== 'function' || fn.__gdHeadlessWrapped) return;
      const wrapped = function (this: unknown, ...args: any[]) {
        const result = fn.apply(this, args);
        captureIdentifyUserId(args[0]);
        mergeIdentify(resolveIdentifyTraits(args));
        getActiveHeadlessController()?.notifyIdentify();
        return result;
      };
      (wrapped as any).__gdHeadlessWrapped = true;
      g.identify = wrapped;
    } catch {
      /* identify not available yet */
    }
  };
  ensureWrapped();
  window.setInterval(() => {
    ensureWrapped();
    ensureOnSurface(getdemoEmitter);
    ensureInstructionSurface();
  }, URL_POLL_MS);
};

// API-only bot: no launcher/widget/iframe. The browser-client is hosted on the host page
// itself with a direct WebSocket to the backend, registered as the flow executor for this
// visitor. Started at most once per page — the connection survives SPA navigation.
const startHeadlessMode = (bot: CandidateBot, companyId: string, debug: boolean): void => {
  if (getActiveHeadlessController()) return;
  captureIdentifyFromQueue();
  if (!WEBSOCKET_ENDPOINT) {
    if (debug) console.log('[Getdemo] headless: missing websocket endpoint — skipped.');
    return;
  }
  const controller = startHeadless({
    botId: bot.id,
    companyId,
    wsUrl: WEBSOCKET_ENDPOINT,
    allowedDomains: resolveHeadlessAllowedDomains(bot),
    getExternalUserId: () => identifyUserId,
    debug
  });
  if (controller) {
    armHeadlessIdentifyRebroadcast();
  }
};

// The format the loader should mount (drives the player's ?mode). Lives per-bot in the
// embed-config (sourced from BotIframeConfig). Defaults to 'widget' when absent (e.g. a
// pinned gd-bot-id not present in the candidate set, or a v2 bot without an iframe config).
const formatForBot = (config: EmbedConfig | null, botId?: string | null): string => {
  const bots = config?.copilot?.bots;
  if (!botId || !Array.isArray(bots)) return 'widget';
  const found = bots.find(b => b.id === botId);
  return found?.embedFormat || 'widget';
};

const readCachedConfig = (companyId: string): EmbedConfig | null => {
  try {
    const raw = localStorage.getItem(`getdemo_embed_config_${companyId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number' || Date.now() - parsed.ts > CONFIG_TTL_MS) {
      return null;
    }
    return parsed.data as EmbedConfig;
  } catch {
    return null;
  }
};

const cacheConfig = (companyId: string, data: EmbedConfig): void => {
  try {
    localStorage.setItem(
      `getdemo_embed_config_${companyId}`,
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch {
    /* localStorage unavailable */
  }
};

const fetchConfig = async (companyId: string): Promise<EmbedConfig | null> => {
  const cached = readCachedConfig(companyId);
  if (cached) return cached;
  try {
    const response = await fetch(
      `${BACKEND_ENDPOINT}/public/v1/embed-config/${encodeURIComponent(companyId)}`
    );
    if (!response.ok) return null;
    const body = await response.json();
    const data = (body && body.data ? body.data : body) as EmbedConfig;
    cacheConfig(companyId, data);
    return data;
  } catch {
    return null;
  }
};

const resolveCopilotBotId = (config: EmbedConfig | null, debug: boolean): string | null => {
  const copilot = config?.copilot;
  if (!copilot?.enabled) return null;
  const candidates = Array.isArray(copilot.bots) ? copilot.bots : [];
  const defaultBotId = copilot.defaultBotId ?? copilot.botId ?? null;
  // No rule-aware candidate set (legacy config) → just the default.
  if (!candidates.length) return defaultBotId;
  return resolveBot(candidates, defaultBotId, buildContext(debug));
};

// Re-resolves and swaps the agent (debounced) when the context changes after load:
// SPA navigation (URL → page rules) and late identify (customParams → audience rules).
// The chat is kept intact mid-conversation by BotWidgetManager.swapBot (skips when open).
// Late-identify bridge. Wraps Getdemo.identify so a post-load call (typical ERP: the visitor's
// data only exists after login/AJAX) still reaches the snapshot, the bot manager and the headless
// controller. It used to live inside setupReresolution, AFTER that function's early return — so a
// company with a single agent and no targeting/segmentation rules never got the bridge and NO
// identify ever reached the Copilot (the traits still reached the guides, which is why it looked
// like "the customer configured identify but the interactions show nothing"). Arming is now
// unconditional; only the audience re-resolution stays behind that guard, via onLateIdentify.
//
// A interceptação é por ACESSOR (`installIdentifyInterceptor`), não por atribuição: quem
// reatribuir `Getdemo.identify` depois — o guia faz isso em `GuideManager.processQueue()` — é
// capturado no mesmo instante. Com a reinstalação periódica que havia antes, existia uma janela
// de até `URL_POLL_MS` em que o identify do cliente chegava ao guia e sumia para o Copilot, e o
// agente ficava preso em "sem identify" até o próximo F5. O poll continua, mas só para o caso de
// o objeto `window.Getdemo` INTEIRO ser substituído, quando o acessor morre junto com ele.
const identifyListeners: Array<() => void> = [];
let identifyBridgeArmed = false;

const onLateIdentify = (listener: () => void): void => {
  identifyListeners.push(listener);
};

// O que acontece a CADA identify, seja qual for a implementação por baixo.
const onIdentifyCaptured = (args: unknown[]): void => {
  captureIdentifyUserId(args[0]);
  mergeIdentify(resolveIdentifyTraits(args));
  // Re-seed the bot manager so a post-login identify reaches the bot too (live-pushed to a
  // mounted player, or flushed on the player's gd-ready when it is still mounting).
  BotWidgetManager.getInstance().setHostCustomParams(identifySnapshot);
  // ...and the chat of a version-3 agent the same way: pushed now, flushed on its gd-ready.
  identifyChat(identifySnapshot);
  getActiveHeadlessController()?.notifyIdentify();
  identifyListeners.forEach(listener => {
    try {
      listener();
    } catch {
      /* one listener must not break the others */
    }
  });
};

const armIdentifyBridge = (): void => {
  if (identifyBridgeArmed) return;
  identifyBridgeArmed = true;

  const ensureIdentifyWrapped = () => {
    try {
      const g = (window as any).Getdemo;
      if (!g) return;
      installIdentifyInterceptor(g, onIdentifyCaptured);
    } catch {
      /* identify not available yet */
    }
  };

  ensureIdentifyWrapped();
  window.setInterval(() => {
    ensureIdentifyWrapped();
    // Re-anexa `Getdemo.instruction` na MESMA janela em que o identify é re-embrulhado: o SDK
    // substitui `window.Getdemo` inteiro durante o init, e sem isto a chamada que cair aí se perde.
    ensureInstructionSurface();
  }, URL_POLL_MS);
};

const setupReresolution = (
  config: EmbedConfig,
  appUrl: string | undefined,
  debug: boolean,
  initialBotId: string,
  companyId: string
): void => {
  const candidates = config.copilot?.bots ?? [];
  const hasRules = candidates.some(
    b =>
      (b.pageTargeting && b.pageTargeting.rules?.length) ||
      (b.audienceSegmentation && b.audienceSegmentation.audienceType === 'custom')
  );
  if (candidates.length < 2 && !hasRules) return;

  let currentBotId = initialBotId;
  let timer: number | undefined;
  const manager = BotWidgetManager.getInstance();
  const reResolve = () => {
    const next = resolveCopilotBotId(config, debug);
    publishAvailability(next, next ? findCandidateBot(config, next) : null);
    if (!next) {
      if (manager.hide() || hideChat()) {
        currentBotId = '';
        if (debug) console.log('[Getdemo] no agent eligible here — widget hidden');
      }
      return;
    }
    const nextBot = findCandidateBot(config, next);
    if (nextBot && shouldStartHeadless(nextBot)) {
      if ((manager.hide() || hideChat()) && debug) {
        console.log('[Getdemo] resolved agent is API-only — widget hidden.');
      }
      startHeadlessMode(nextBot, companyId, debug);
      currentBotId = next;
      return;
    }
    // A version-3 agent has the chat, the others the player. The kind that is NOT the resolved
    // one goes away first (a company with both); if it is open, hide() refuses and the agent
    // stays, as swapBot does mid-conversation.
    const wantsChat = isNewVersion(nextBot);
    const otherMounted = wantsChat ? manager.isMounted() : isChatMounted();
    if (otherMounted && !(wantsChat ? manager.hide() : hideChat())) return;
    const mountedNow = wantsChat ? isChatMounted() : manager.isMounted();
    if (next === currentBotId && mountedNow) return;
    if (!mountedNow) {
      startBot(next, appUrl, formatForBot(config, next), nextBot, companyId);
      currentBotId = next;
      // É AQUI que o caso da Getrak fecha: audiência custom + identify ~5s pós-login. O clique
      // no botão aconteceu antes de existir agente; esta é a re-resolução disparada pelo
      // identify tardio, e a intenção pendente vira abertura.
      getChatTrigger().notifyResolved();
      if (debug) console.log('[Getdemo] agent eligible again — widget shown:', next);
      return;
    }
    if (wantsChat) {
      if (swapChat(next, chatModeOf(formatForBot(config, next)))) {
        currentBotId = next;
        if (debug) console.log('[Getdemo] swapped agent to', next);
      }
      return;
    }
    const swapped = manager.swapBot(next, appUrl, formatForBot(config, next));
    if (swapped) {
      // O iframe foi renavegado: o player novo ainda não sabe da instrução, e quem a conhece é o
      // snapshot desta página. (O manager não zera `seededInstruction` no swap — mesma página,
      // mesmo usuário —, mas re-semear aqui torna o caminho explícito e independente disso.)
      manager.setHostInstruction(getInstructionSnapshot());
      currentBotId = next;
      if (debug) console.log('[Getdemo] swapped agent to', next);
    }
  };
  const schedule = () => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(reResolve, SWAP_DEBOUNCE_MS);
  };

  // SPA navigation. We detect URL changes from any mechanism: the history patches fire
  // immediately for well-behaved routers, while the hashchange listener + interval poll are
  // a safety net for routers that cached history.pushState before this async loader patched
  // it, hash routers, or any navigation the patches don't observe. onUrlChanged dedupes by
  // comparing against the last seen URL, so overlapping triggers schedule a single re-resolve.
  let lastUrl = window.location.href;
  const onUrlChanged = () => {
    if (window.location.href === lastUrl) return;
    lastUrl = window.location.href;
    schedule();
  };

  (['pushState', 'replaceState'] as const).forEach(type => {
    const original = history[type];
    history[type] = function (this: History, ...args: any[]) {
      const result = original.apply(this, args as any);
      onUrlChanged();
      return result;
    } as any;
  });
  window.addEventListener('popstate', onUrlChanged);
  window.addEventListener('hashchange', onUrlChanged);

  // A late identify must also re-resolve the audience — but the bridge itself is armed
  // unconditionally in armIdentifyBridge(), OUTSIDE this function's early return.
  onLateIdentify(schedule);

  window.setInterval(onUrlChanged, URL_POLL_MS);
};

const onDomReady = (cb: () => void): void => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cb, { once: true });
  } else {
    cb();
  }
};

const main = async (): Promise<void> => {
  const tag = getScriptTag();
  const companyId = tag?.getAttribute('gd-company-id') || undefined;
  const botIdAttr = tag?.getAttribute('gd-bot-id') || undefined;
  const appUrl = tag?.getAttribute('gd-app-url') || undefined;
  const debug = isDebug();

  // Snapshot identify now, before the await below yields and the SDK drains the queue.
  captureIdentifyFromQueue();
  // E intercepta o `identify` JÁ, não só ao resolver o agente lá embaixo. Entre este ponto e a
  // resolução há dois awaits (config e DOMContentLoaded) — segundos, numa aplicação pesada. Uma
  // chamada caída nesse intervalo só sobrevivia pela fila `q`, e o guia esvazia a fila quando
  // termina de subir (`GuideManager.processQueue`). Quando o guia ganhava a corrida, as traits
  // sumiam de vez: audiência reprovada no load e nenhuma re-resolução depois, porque não havia
  // mais identify nenhum para observar. Armar aqui fecha essa janela — a maior das duas.
  armIdentifyBridge();
  // Expose Getdemo.on early so host code can subscribe to 'status'/'flow' before (and
  // regardless of) the headless connection being established.
  ensureOnSurface(getdemoEmitter);
  // Idem para open/close: o botão do cliente pode ser clicado antes de qualquer agente existir,
  // e a intenção precisa de alguém para segurá-la.
  ensureChatSurface(getChatTrigger());
  // Mesma razão do `ensureOnSurface` acima: a superfície tem de existir antes do primeiro `await`,
  // e a fila genérica tem de ser drenada enquanto ainda tem dado.
  ensureInstructionSurface();
  // Chamada TARDIA (pós-login, troca de administradora): empurra o valor novo para o player já
  // montado — ou o deixa pronto para o flush do `gd-ready`, quando ele ainda está montando.
  onInstructionChange(value => {
    BotWidgetManager.getInstance().setHostInstruction(value);
  });

  // Reply to descendant frames' ownership probes ASAP so a nested getdemo defers to us.
  answerOwnershipProbes();
  // Does this frame own the floating bot widget? Only the owner boots the bot / browser
  // client — a nested copy would strand the real widget's browser session (see helper).
  const ownsWidget = await ownsFloatingWidget();
  if (debug) console.log('[Getdemo] ownsFloatingWidget:', ownsWidget);

  // Without a company id we cannot consult disabledBotIds — hydrate now and rely on
  // BotPlayer/getPublic (siteEnabled) to keep a deactivated agent blank.
  if (ownsWidget && !companyId) {
    seedInstructionIntoManager();
    BotWidgetManager.getInstance().hydrateEmbeddedIframeIfPresent();
  }

  if (debug) {
    console.log('[Getdemo] gdDebug ON', { companyId, botIdAttr, appUrl });
  }

  // Explicit attributes always win (legacy installs and power users) — pinned, no routing.
  if (botIdAttr) {
    if (debug) console.log('[Getdemo] pinned agent via gd-bot-id (no routing):', botIdAttr);
    if (companyId) startGuide();
    if (ownsWidget) startBot(botIdAttr, appUrl);
    return;
  }

  if (!companyId) {
    if (debug) console.log('[Getdemo] no gd-company-id nor gd-bot-id on the script tag.');
    return;
  }

  // Single-script install: the platform decides which products are active.
  const config = await fetchConfig(companyId);
  if (debug) console.log('[Getdemo] embed-config:', config);

  if (ownsWidget) {
    hydrateEmbeddedIframeUnlessDisabled(config?.copilot?.disabledBotIds, debug);
  }

  if (config?.tourGuide?.enabled) {
    startGuide();
  }
  if (config?.copilot?.enabled && !ownsWidget) {
    if (debug) {
      console.log(
        '[Getdemo] copilot enabled but this frame does not own the widget — bot skipped.'
      );
    }
  } else if (config?.copilot?.enabled) {
    const candidateCount = Array.isArray(config.copilot.bots) ? config.copilot.bots.length : 0;
    if (debug) {
      console.log('[Getdemo] copilot enabled', {
        defaultBotId: config.copilot.defaultBotId ?? config.copilot.botId ?? null,
        candidates: candidateCount
      });
    }
    onDomReady(() => {
      if (document.getElementById('getdemo-bot-player')) {
        if (debug) {
          console.log('[Getdemo] embedded iframe present — floating copilot widget skipped.');
        }
        return;
      }
      // Arm the late-identify bridge BEFORE resolving. On a page where nothing is eligible at
      // load — every agent gated by an audience the visitor only satisfies after login — startBot
      // never runs, so the armIdentifyBridge() call inside it is unreachable and the late
      // identify is never observed: no re-resolution, widget never mounts. Idempotent, so the
      // call in startBot stays harmless.
      armIdentifyBridge();
      const botId = resolveCopilotBotId(config, debug);
      const resolvedBot = findCandidateBot(config, botId);
      if (botId && resolvedBot && shouldStartHeadless(resolvedBot)) {
        if (debug) console.log('[Getdemo] resolved agent is API-only — headless mode:', botId);
        startHeadlessMode(resolvedBot, companyId, debug);
      } else if (botId) {
        startBot(botId, appUrl, formatForBot(config, botId), resolvedBot, companyId);
      } else if (debug) {
        console.log('[Getdemo] no agent eligible on this page at load.');
      }
      publishAvailability(botId, resolvedBot);
      // Um `open()` que chegou antes desta resolução fica pendente; agora que ela terminou, abre.
      getChatTrigger().notifyResolved();
      // Always arm re-resolution when copilot is enabled: a page where nothing is eligible at
      // load can become eligible after SPA navigation (and vice-versa). setupReresolution
      // self-skips when there is a single rule-less bot (nothing to re-evaluate).
      setupReresolution(config, appUrl, debug, botId ?? '', companyId);
    });
  } else {
    // Resposta definitiva, e não silêncio: sem agente nenhum nesta company o cliente precisa
    // saber que pode esconder o botão de vez.
    publishAvailability(null, null);
    if (debug) console.log('[Getdemo] copilot is disabled in the embed-config.');
  }
};

void main();
